import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Loads the Google Maps JS API (places library) exactly once and returns a
 * promise that resolves when window.google.maps.places is available.
 */
let _mapsLoaderPromise = null;

function pacSuggestionsOpen() {
  return Array.from(document.querySelectorAll(".pac-container")).some(
    (el) => el.offsetParent !== null && el.querySelector(".pac-item"),
  );
}

function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google.maps);
  }
  if (_mapsLoaderPromise) return _mapsLoaderPromise;
  if (!apiKey) return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));

  _mapsLoaderPromise = new Promise((resolve, reject) => {
    // Inject script only once. Use loading=async + importLibrary for proper
    // async place-library loading (recommended by Google).
    const existing = document.querySelector('script[data-google-maps-loader="1"]');
    const waitForPlaces = async () => {
      try {
        // Modern API: dynamically import the places library.
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          await window.google.maps.importLibrary("places");
        }
        // Poll up to 8s in case importLibrary is unavailable (very old SDK).
        const start = Date.now();
        while (!(window.google && window.google.maps && window.google.maps.places)) {
          if (Date.now() - start > 8000) throw new Error("places library timed out");
          await new Promise((r) => setTimeout(r, 100));
        }
        resolve(window.google.maps);
      } catch (e) {
        reject(e);
      }
    };
    if (existing) {
      if (window.google && window.google.maps) {
        waitForPlaces();
      } else {
        existing.addEventListener("load", waitForPlaces);
        existing.addEventListener("error", (e) => reject(e));
      }
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "1";
    script.onload = waitForPlaces;
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return _mapsLoaderPromise;
}

/**
 * Drop-in replacement for <Input> that attaches Google Places Autocomplete.
 * Props: value, onChange(text), onPlaceSelected({name, lat, lon}), placeholder,
 * className, id, required, testId, iconLeft (JSX)
 */
export default function PlacesAutocompleteInput({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  className = "",
  id,
  required = false,
  testId,
  countries = ["hr"],
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [ready, setReady] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !inputRef.current) return;
        try {
          acRef.current = new maps.places.Autocomplete(inputRef.current, {
            fields: ["formatted_address", "geometry", "name"],
            componentRestrictions: countries && countries.length ? { country: countries } : undefined,
          });
          acRef.current.addListener("place_changed", () => {
            const place = acRef.current.getPlace();
            if (!place) return;
            // Fallback to whatever Google wrote into the DOM input — keeps
            // the controlled React state in sync even when the place object
            // lacks formatted_address (e.g. Enter on a raw query).
            const text = place.formatted_address || place.name
              || (inputRef.current && inputRef.current.value) || "";
            if (text) onChange && onChange(text);
            if (place.geometry && place.geometry.location && onPlaceSelected) {
              onPlaceSelected({
                name: text,
                lat: place.geometry.location.lat(),
                lon: place.geometry.location.lng(),
              });
            }
          });
          setReady(true);
        } catch (e) {
          // Fail silently — input still works as a plain text field.
          // eslint-disable-next-line no-console
          console.warn("Places Autocomplete init failed:", e);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("Google Maps JS failed to load, falling back to plain input:", e);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return (
    <Input
      ref={inputRef}
      id={id}
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange && onChange(e.target.value)}
      onKeyDown={(e) => {
        // While suggestions are open, Enter/Tab must pick a place — not
        // submit the form or jump focus to the next field (DATE).
        if ((e.key === "Enter" || e.key === "Tab") && ready && pacSuggestionsOpen()) {
          e.preventDefault();
        }
      }}
      placeholder={placeholder}
      className={className}
      required={required}
      autoComplete="off"
      aria-autocomplete={ready ? "list" : undefined}
    />
  );
}
